import React, { useCallback, useRef, useState } from "react";
import { AccrualReviewApproveResponse } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { MatchableItem, MatchItemType } from "./types";
import { AllocationDrawerLayout } from "./editAllocation/AllocationDrawerLayout";
import { TransactionAllocationEditor } from "./editAllocation/TransactionAllocationEditor";
import { documentMatchItemType } from "./editAllocation/allocationHelpers";
import { useApproveAllocationEntry } from "./editAllocation/useApproveAllocationEntry";
import { useAllocationView } from "./editAllocation/useAllocationView";
import { usePaymentAmounts } from "./editAllocation/usePaymentAmounts";
import { useTransactionAllocation } from "./editAllocation/useTransactionAllocation";
import { unmatchedSelectionItemIds } from "./schedule/matchResolutions";
import { useAccrualMatch } from "./useAccrualMatch";

interface Props {
    open: boolean;
    onClose: () => void;
    sourceItem: MatchableItem | null;
    onSaveSuccess?: (response: AccrualReviewApproveResponse) => void;
}

export const EditAllocationDrawer: React.FC<Props> = ({
    open,
    onClose,
    sourceItem,
    onSaveSuccess,
}) => {
    const {
        selectedItems,
        removeItem,
        sourceTransaction,
        currentTxLineItemMatchAmounts,
        isGlFirst,
    } = useAccrualMatch();
    const { workspaceId } = useScope();
    const { toast } = useToaster();
    const [isLoading, setIsLoading] = useState(false);
    const isSubmittingRef = useRef(false);

    const { paymentAmounts, handlePaymentChange } = usePaymentAmounts({
        selectedItems,
        transactionAmount: sourceTransaction?.amount ?? 0,
        currentTxLineItemMatchAmounts,
    });
    const allocation = useTransactionAllocation({
        sourceTransaction,
        sourceItem,
        selectedItems,
        paymentAmounts,
    });
    const { remainingState, hasAccrualLedger } = allocation;

    const allocationMatchItemType = documentMatchItemType(
        selectedItems.filter((item) => item.type !== MatchItemType.TRANSACTION),
    );
    const viewState = useAllocationView({
        isGlFirst,
        hasAccrualLedger,
        matchItemType: allocationMatchItemType,
    });

    const isSaveEnabled = allocation.isAllocationValid && !isLoading;

    const { approveAllocation, scheduleResolutionModals } =
        useApproveAllocationEntry();

    const handleSave = useCallback(async () => {
        if (!workspaceId || isSubmittingRef.current || !isSaveEnabled) {
            return;
        }
        isSubmittingRef.current = true;
        setIsLoading(true);
        try {
            await approveAllocation({
                workspaceId,
                approval: {
                    body: allocation.body,
                    hasRemainingAllocations: allocation.hasRemainingAllocations,
                    selectedItems,
                },
                onUnmatched: (resolutions) => {
                    for (const itemId of unmatchedSelectionItemIds(
                        resolutions,
                    )) {
                        removeItem(itemId);
                    }
                },
                onApproved: (response) => {
                    toast("Match saved");
                    if (onSaveSuccess) {
                        onSaveSuccess(response);
                    } else {
                        onClose();
                    }
                },
            });
        } catch {
            // toasted by approveWithScheduleResolutions
        } finally {
            isSubmittingRef.current = false;
            setIsLoading(false);
        }
    }, [
        workspaceId,
        isSaveEnabled,
        selectedItems,
        allocation.body,
        allocation.hasRemainingAllocations,
        onClose,
        onSaveSuccess,
        toast,
        approveAllocation,
        removeItem,
    ]);

    if (!sourceTransaction || !sourceItem) {
        return null;
    }

    return (
        <>
            <AllocationDrawerLayout
                open={open}
                onClose={onClose}
                viewState={viewState}
                hasAccrualLedger={hasAccrualLedger}
                totals={allocation}
                canResolveDifference={!remainingState.isVisible}
                onResolveDifference={allocation.showRemaining}
                secondaryAction={{ label: "Add match", onClick: onClose }}
                isSaveEnabled={isSaveEnabled}
                onSave={handleSave}
            >
                <TransactionAllocationEditor
                    sourceItem={sourceItem}
                    selectedItems={selectedItems}
                    allocation={allocation}
                    paymentAmounts={paymentAmounts}
                    onPaymentChange={handlePaymentChange}
                    onRemoveSelectedMatch={removeItem}
                    matchItemType={allocationMatchItemType}
                    view={viewState.allocationView}
                    onViewChange={viewState.setAllocationView}
                />
            </AllocationDrawerLayout>
            {scheduleResolutionModals}
        </>
    );
};
